import React, { useState, useContext, useEffect, useRef } from "react";
import { ChatContext } from "src/scenes/Chat/ChatContext";
import ChatInputMessages from "src/scenes/Chat/components/ChatInputMessages";
import LeadershipEmployees from "src/scenes/Chat/components/LeadershipEmployees";
import {
    Container, ChatBox, Header, MessagesContainer, ChatInputContainer, EditChatButton, Dropdown, Dot
} from "src/scenes/Chat/components/styled/chatMessagesStyled";
import { FlexContainer, Text, Toast } from "src/components";
import DropdownActionsBox from "src/scenes/Chat/components/DropdownActionsBox";
import { UserContext } from "src/scenes/App/UserContext";
import { MESSAGE_CLIENTS, MESSAGE_EMPLOYEES } from "src/constants/permissions";
import NoActiveChats from "src/scenes/Chat/components/children/chatmessages/NoActiveChats";
import { IsTypingBox } from "src/scenes/Chat/components/children/chatmessages/IsTypingBox";
import ViewGroupChatModal from "src/scenes/Chat/components/ViewGroupChatModal";
import EditGroupChatModal from "src/scenes/Chat/components/EditGroupChatModal";
import ChatRoomTimeline from "src/scenes/Chat/components/children/ChatRoomTimeline";
import { isChatActionAllowed } from "src/constants/permissions";
import { USER_TYPE_CLIENTS, USER_TYPE_EMPLOYEES } from "src/constants/chat";
import { useFilePreviews } from "src/scenes/Chat/hooks/chat/useFilePreview";
import styled from "styled-components";
import { portalColors } from "src/components";
import VideoCallIcon from "src/img/new/chats/video-white.png";
import { canUseVideoCalling } from "src/utils/checkedInUser";

const CenteredContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const HeaderTitleButton = styled.button`
    border: 0;
    background: transparent;
    padding: 0;
    cursor: ${p => p.disabled ? "default" : "pointer"};
    text-align: left;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.625rem;
`;

const CallButton = styled.button`
    width: ${p => p.$wide ? "auto" : "2.75rem"};
    height: 2.75rem;
    min-width: ${p => p.$wide ? "7rem" : "2.75rem"};
    padding: ${p => p.$wide ? "0 1rem" : "0"};
    border-radius: 1.375rem;
    border: 1px solid ${p => p.$variant === "ghost" ? portalColors.border : "transparent"};
    background: ${p => p.$variant === "ghost"
        ? portalColors.surface
        : "linear-gradient(135deg, #0B66E4 0%, #5B6EF5 52%, #7C3AED 100%)"};
    color: ${p => p.$variant === "ghost" ? portalColors.text : "#fff"};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 800;
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    opacity: ${p => p.disabled ? 0.55 : 1};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    box-shadow: ${p => p.$variant === "ghost" ? "none" : "0 0.625rem 1.25rem rgba(91, 110, 245, 0.24)"};
    transition: transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: ${p => p.$variant === "ghost" ? "none" : "0 0.875rem 1.5rem rgba(91, 110, 245, 0.3)"};
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:focus-visible {
        outline: 3px solid rgba(91, 110, 245, 0.28);
        outline-offset: 2px;
    }
`;

const CallButtonIcon = styled.img`
    width: 1.5rem;
    height: 1.5rem;
    display: block;
`;

const ActiveCallBanner = styled.div`
    margin: 0.75rem 1.8125rem 0;
    padding: 0.75rem 1rem;
    border: 1px solid ${portalColors.border};
    border-radius: 0.75rem;
    background: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
`;

const ActiveCallBannerText = styled.div`
    min-width: 0;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
`;

const canJoinActiveCall = (currentChat, activeGroupCall, callState) => Boolean(
    currentChat &&
    activeGroupCall?.isActive === true &&
    (activeGroupCall?.groupCallId || activeGroupCall?.callId) &&
    (
        activeGroupCall?.chatUuid === currentChat.uuid ||
        activeGroupCall?.chat?.uuid === currentChat.uuid ||
        activeGroupCall?.matrixRoomId === currentChat.matrixRoomId
    ) &&
    callState?.status === "idle"
);

const RunningCallNotice = ({ currentChat }) => {
    const user = useContext(UserContext);
    const { useChatCalls: ctxUseChatCalls } = useContext(ChatContext);
    const { activeGroupCall, callState, isSendingCallAction, joinGroupCall } = ctxUseChatCalls;

    if (!canUseVideoCalling(user) || !canJoinActiveCall(currentChat, activeGroupCall, callState)) {
        return null;
    }

    return (
        <ActiveCallBanner role="status">
            <ActiveCallBannerText>A video call is currently running in this chat.</ActiveCallBannerText>
            <CallButton
                type="button"
                title="Join video call"
                $wide
                disabled={isSendingCallAction}
                onClick={() => joinGroupCall(currentChat)}
            >
                Join
            </CallButton>
        </ActiveCallBanner>
    );
};

const ChatMessageContainer = () => {
    const messagesContainerRef = useRef(null);
    const pendingRemovalNotificationKeyRef = useRef("");
    const [showViewGroupChatModal, setShowViewGroupChatModal] = useState(false);
    const [openedTimeline, setOpenedTimeline] = useState(null);
    const [isLookingForLeadershipToChat, setIsLookingForLeadershipToChat] = useState(false);
    const { previews, setPreviews, handleDrop } = useFilePreviews();
    const [isDragging, setIsDragging] = useState(false);
    const { 
        matrixClient,
        currentMatrixUserId,
        useChatMessage: ctxUseChatMessage,
        useDeleteChat: ctxUseDeleteChat,
        useEditChat: ctxUseEditChat,
        useTypingListener: ctxUseTypingListener,
        useActiveChats: ctxUseActiveChats,
        useCreateChatModal: ctxUseCreateChatModal,
        useChatListUnread: ctxUseChatListUnread
    } = useContext(ChatContext);

    const { 
        isFetchingActiveChats,
        selectedChatListTypeOrLeadershipUuid,
        lookingForLeadershipToChat,
        selectedChatListTypeOrLeadershipUuidLabel
    } = ctxUseActiveChats;

    const { 
        isTyping, 
        typingMember, 
        typingRoomId 
    } = ctxUseTypingListener;

    const {
        openedMatrixChat,
        toggleDropdown,
        setToggleDropdown,
        displayOtherUserProfileModal
    } = ctxUseChatMessage;

    useEffect(() => {
        const pendingRemovalCount = openedMatrixChat?.users?.filter(user =>
            user.currentMember && user.requestingLeave
        ).length || 0;
        const isGroupOwner = openedMatrixChat?.ownerUuid &&
            `@${openedMatrixChat.ownerUuid}:memotivationapp.com` === currentMatrixUserId;
        const notificationKey = isGroupOwner && pendingRemovalCount > 0
            ? `${openedMatrixChat.uuid}:${pendingRemovalCount}`
            : "";

        if (notificationKey && pendingRemovalNotificationKeyRef.current !== notificationKey) {
            Toast.info(
                `${pendingRemovalCount} ${pendingRemovalCount === 1 ? "member has" : "members have"} requested removal.`
            );
        }
        pendingRemovalNotificationKeyRef.current = notificationKey;
    }, [currentMatrixUserId, openedMatrixChat]);

    const { 
        handleShowDeleteConfirmationModal 
    } = ctxUseDeleteChat;

    const {
        showEditGroupChatModal,
        setShowEditGroupChatModal
    } = ctxUseEditChat;

    const { 
        showCreateChatModalOrShowLeadershipEmployees,
        createChatWithLeadership
    } = ctxUseCreateChatModal;
    
    const {
        updateActiveChatListTotalUnread,
    } = ctxUseChatListUnread;

    useEffect(() => {
        if (!lookingForLeadershipToChat) {
            setIsLookingForLeadershipToChat(false);
            return;
        }

        const isNonLeadership = [USER_TYPE_CLIENTS, USER_TYPE_EMPLOYEES].includes(lookingForLeadershipToChat.uuid);
        setIsLookingForLeadershipToChat(isNonLeadership ? false : true);

    }, [lookingForLeadershipToChat]); //see useCreateChatModal::showCreateChatModalOrShowLeadershipEmployees()
    
    // added to reflect that typing state in the UI sidebar.
    useEffect(() => {
        if (!openedMatrixChat || !typingRoomId) return;

        if (openedMatrixChat.matrixRoomId === typingRoomId) {
            openedMatrixChat.setTypingStatus(isTyping, typingMember);
        } else {
            openedMatrixChat.setTypingStatus(false);
        }
    }, [isTyping, typingRoomId, openedMatrixChat, typingMember]);
    
    useEffect(() => {
        if (!matrixClient || !openedMatrixChat) return;
        // Received: When someone else reads *your* message
        const handleReceipt = async (room) => {
            if (room.roomId !== openedMatrixChat.matrixRoomId) return;

            const roomTimeline = room.getLiveTimeline()?.getEvents() || [];
            const lastEvent = roomTimeline[roomTimeline.length - 1];

            if (lastEvent) {
                try {
                    await matrixClient.sendReadReceipt(lastEvent);
                    room.setUnreadNotificationCount(0);
                    updateActiveChatListTotalUnread();
                } catch (error) {
                    console.error("Failed to send read receipt on Receipt event:", error);
                }
            }
        };

        matrixClient.on("Receipt", handleReceipt);
        return () => {
            matrixClient.off("Receipt", handleReceipt);
        };
    }, [matrixClient, openedMatrixChat, updateActiveChatListTotalUnread]);


    useEffect(() => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    }, [openedTimeline, previews]);

    return (  
        <>
            {
                isLookingForLeadershipToChat  
                    ?
                    <LeadershipEmployees
                        createChatWithLeadership={createChatWithLeadership}
                        selectedChatListTypeOrLeadershipUuidLabel={selectedChatListTypeOrLeadershipUuidLabel}
                        isChatListEmpty={ (openedMatrixChat) && true }
                    /> 
                    :
                    <>
                        <Container >
                            <ChatBox 
                                $isDragging={isDragging}
                                tabIndex={0}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    if (!isDragging) setIsDragging(true);
                                }}
                                onDragLeave={() => setIsDragging(false)}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setIsDragging(false);
                                    handleDrop(e);
                                }}
                                onFocus={async () => {
                                    /** Send read receipt only when the user is actively viewing this room.
                                     * Set it here and remove triggering it on useMatrixClient to prevent marking messages as read in rooms the user hasn't actually opened.
                                     * Send: You (the user) are marking messages as read when viewing the chat
                                    */
                                    if (!openedMatrixChat || !matrixClient) return;
                                    const room = matrixClient.getRoom(openedMatrixChat.matrixRoomId);
                                    if (!room) return;

                                    const events = room.getLiveTimeline()?.getEvents() || [];
                                    const lastEvent = [...events].reverse().find(e => !!e.getId());

                                    if (lastEvent) {
                                        try {
                                            await matrixClient.sendReadReceipt(lastEvent);
                                        } catch (err) {
                                            console.warn("sendReadReceipt failed", err);
                                        }
                                        // Force clear unread notification count locally then re-cal
                                        room.setUnreadNotificationCount(0);
                                        updateActiveChatListTotalUnread();
                                    }
                                }}>
                                <ChatBoxHeader
                                    currentChat={openedMatrixChat}
                                    setShowEditGroupChatModal={setShowEditGroupChatModal}
                                    setShowViewGroupChatModal={setShowViewGroupChatModal}
                                    setToggleDropdown={setToggleDropdown}
                                    toggleDropdown={toggleDropdown}
                                    showUserProfile={displayOtherUserProfileModal}
                                    handleMatrixChatShowDeleteConfirmationModal={ () => handleShowDeleteConfirmationModal(openedMatrixChat) }
                                />
                                <ChatBoxBody
                                    setOpenedTimeline={setOpenedTimeline} 
                                    currentChat={openedMatrixChat} 
                                    isChatActionAllowed={isChatActionAllowed} 
                                    selectedChatListTypeOrLeadershipUuid={selectedChatListTypeOrLeadershipUuid} 
                                    showCreateChatModalOrShowLeadershipEmployees={showCreateChatModalOrShowLeadershipEmployees} 
                                    isTyping={isTyping}
                                    isFetchingActiveChats={isFetchingActiveChats}
                                >
                                    <RunningCallNotice currentChat={openedMatrixChat} />
                                    <MessagesContainer ref={messagesContainerRef}>
                                        <IsTypingBox 
                                            currentChat={openedMatrixChat} 
                                            isTyping={isTyping} 
                                            typingRoomId={typingRoomId}  
                                            typingMember={typingMember} 
                                            matrixClient={matrixClient}
                                        />
                                        <ChatRoomTimeline 
                                            matrixClient={matrixClient}
                                            roomTimeline={openedTimeline} 
                                            currentChat={openedMatrixChat}
                                            currentMatrixUserId={currentMatrixUserId}
                                        />
                                    </MessagesContainer>
                                    <ChatInputContainer>
                                        <ChatInputMessages 
                                            onDragPreviews={previews} 
                                            onDragSetPreviews={setPreviews} 
                                            currentChat={openedMatrixChat} 
                                        />
                                    </ChatInputContainer>
                                </ChatBoxBody>
                            </ChatBox>
                        </Container>
                        { showViewGroupChatModal &&
                            <ViewGroupChatModal 
                                currentChat={openedMatrixChat}
                                showDialog={showViewGroupChatModal}
                                handleClose={() => setShowViewGroupChatModal(false)}
                                displayOtherUserProfileModal={displayOtherUserProfileModal}
                            />
                        }
                        { showEditGroupChatModal &&
                            <EditGroupChatModal 
                                selectedChatListTypeOrLeadershipUuidLabel={[USER_TYPE_CLIENTS, USER_TYPE_EMPLOYEES].includes(selectedChatListTypeOrLeadershipUuid) ? null : selectedChatListTypeOrLeadershipUuidLabel }
                            />
                        }
                    </>
            }
        </>
    );
};

const ChatBoxHeader = ({ 
    currentChat,
    setShowEditGroupChatModal,
    setShowViewGroupChatModal, 
    setToggleDropdown, 
    toggleDropdown, 
    showUserProfile, 
    handleMatrixChatShowDeleteConfirmationModal 
}) => {
    const user = useContext(UserContext);
    const {
        currentMatrixUserId,
        useChatCalls: ctxUseChatCalls
    } = useContext(ChatContext);

    const {
        callState,
        activeGroupCall,
        isSendingCallAction,
        callLobby,
        openCallLobby,
        focusCallWindow,
        joinGroupCall
    } = ctxUseChatCalls;

    const currentChatHasActiveCall = currentChat &&
        callState?.status !== "idle" &&
        (
            callState?.chatUuid === currentChat.uuid ||
            callState?.matrixRoomId === currentChat.matrixRoomId
        );
    const callControlsAreDisabled = !currentChat || isSendingCallAction || callState?.status !== "idle" || callLobby?.isOpen;
    const canJoinRunningVideoCall = canJoinActiveCall(currentChat, activeGroupCall, callState);

    return (
        <Header> 
            { currentChat &&
                <React.Fragment>
                    <HeaderTitleButton
                        type="button"
                        onClick={() => currentChat.isGroupChat ? setShowViewGroupChatModal(true) : showUserProfile()}
                    >
                        <Text color="#4B5155" weight="700" size="1.125rem">{ currentChat.name }</Text>
                    </HeaderTitleButton>
                    <HeaderActions>
                        { canUseVideoCalling(user) &&
                            <React.Fragment>
                                { currentChatHasActiveCall ? (
                                    <CallButton
                                        type="button"
                                        title="Return to call"
                                        $wide
                                        onClick={focusCallWindow}
                                    >
                                        Return to Call
                                    </CallButton>
                                ) : (
                                    <CallButton
                                        type="button"
                                        title="Start call"
                                        $wide
                                        disabled={callControlsAreDisabled || canJoinRunningVideoCall}
                                        onClick={() => openCallLobby(currentChat)}
                                    >
                                        {!isSendingCallAction && <CallButtonIcon src={VideoCallIcon} alt="" />}
                                        {isSendingCallAction ? "Starting..." : "Start Call"}
                                    </CallButton>
                                )}
                                { canJoinRunningVideoCall &&
                                    <CallButton
                                        type="button"
                                        title="Join video call"
                                        $wide
                                        $variant="ghost"
                                        disabled={isSendingCallAction}
                                        onClick={() => joinGroupCall(currentChat)}
                                    >
                                        Join
                                    </CallButton>
                                }
                            </React.Fragment>
                        }
                        { currentChat.isGroupChat ?
                            <React.Fragment>
                                { `@${currentChat.ownerUuid}:memotivationapp.com` === currentMatrixUserId ?
                                    <EditChatButton onClick={() => setShowEditGroupChatModal(true) } enabled={true}>
                                        Edit Group
                                    </EditChatButton>
                                    :
                                    <EditChatButton onClick={() => setShowViewGroupChatModal(true)} enabled={true}>
                                        View Group
                                    </EditChatButton>
                                }
                            </React.Fragment>

                            :
                            <CenteredContent>
                                <Dropdown
                                    onClick={() => setToggleDropdown(!toggleDropdown)}
                                >
                                    <Dot />
                                    <Dot />
                                    <Dot />

                                </Dropdown>
                            </CenteredContent>
                        }
                    </HeaderActions>
                </React.Fragment>
            }
            
            { toggleDropdown &&
                <DropdownActionsBox
                    showUserProfile={showUserProfile}
                    deleteChat={handleMatrixChatShowDeleteConfirmationModal}
                    setToggleDropdown={setToggleDropdown}
                />
            }
        </Header>
    );
};

const ChatBoxBody = ({ children, 
    setOpenedTimeline, 
    currentChat, 
    isChatActionAllowed, 
    selectedChatListTypeOrLeadershipUuid, 
    showCreateChatModalOrShowLeadershipEmployees,
    isTyping,
    isFetchingActiveChats
}) => {
    
    const userContext = useContext(UserContext);
    const hasPermissionToInitiateChat = isChatActionAllowed(MESSAGE_CLIENTS, userContext.permissions) 
    || isChatActionAllowed(MESSAGE_EMPLOYEES, userContext.permissions);

    const { 
        useChatMessage: ctxUseChatMessage
    } = useContext(ChatContext);

    const { 
        isFetchingRoomTimeline,
        roomTimeline
    } = ctxUseChatMessage;
    
    useEffect(() => {
        setOpenedTimeline(roomTimeline);
    }, [roomTimeline, isFetchingRoomTimeline, isTyping, isFetchingActiveChats]);
        
    const thereIsNoExistingChat = !currentChat?.length;

    return (
        <>
            { !currentChat ?
                <NoActiveChatContainer hasPermissionToInitiateChat={hasPermissionToInitiateChat} thereIsNoExistingChat={thereIsNoExistingChat}>
                    <NoActiveChats 
                        selectedChatListTypeOrLeadershipUuid={selectedChatListTypeOrLeadershipUuid}
                        permissions={userContext.permissions}
                        showCreateChatModalOrShowLeadershipEmployees={showCreateChatModalOrShowLeadershipEmployees }
                    />
                </NoActiveChatContainer>
                :
                <>{children}</>
            }
        </>
    );
};

const NoActiveChatContainer = ({ children, hasPermissionToInitiateChat, thereIsNoExistingChat }) => {
    return (
        <FlexContainer height="100%" justifyContent="center" alignItems="center">
            { !hasPermissionToInitiateChat && <div>No available chat</div> }

            { hasPermissionToInitiateChat && thereIsNoExistingChat && <>{children}</>
            }
            { hasPermissionToInitiateChat && !thereIsNoExistingChat &&
                <Text color="#8c8c8c">Select a chat to see its messages.</Text>
            }
        </FlexContainer>
    );
};

export default ChatMessageContainer; 
